import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ShieldCheck, Users, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { useWorkspace } from "../lib/WorkspaceContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { tokenFromLocation } from "../lib/tokenFromLocation";

export default function InvitationAccept() {
  // Token travels in the URL fragment (never sent to servers/analytics);
  // the path param remains as a fallback for links from older emails.
  const { token: pathToken } = useParams();
  const token = tokenFromLocation(useLocation(), pathToken);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refresh, switchTo } = useWorkspace();

  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    if (user === null) return; // auth still loading (null=checking, false=anon, obj=signed in)
    if (!user) {
      // Park the token and bounce to login. Login will read it back.
      sessionStorage.setItem("pendingInvitationToken", token);
      navigate("/login");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/workspaces/invitations/${token}`);
        if (!cancelled) setInvitation(res.data);
      } catch (e) {
        if (!cancelled) setError(formatApiErrorDetail(e.response?.data?.detail) || "Invitation not found");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, user, navigate]);

  const handleAccept = async () => {
    setAccepting(true);
    try {
      const res = await api.post(`/workspaces/invitations/${token}/accept`);
      toast.success(`Joined ${res.data.workspace?.name || "the workspace"}`);
      setAccepted(true);
      await refresh();
      if (res.data.workspace?.id) switchTo(res.data.workspace.id);
      setTimeout(() => navigate("/dashboard"), 800);
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || "Could not accept invitation";
      toast.error(msg);
      setError(msg);
    } finally {
      setAccepting(false);
    }
  };

  const isEmailMismatch =
    invitation && user && invitation.email.toLowerCase() !== (user.email || "").toLowerCase();

  return (
    <div className="min-h-screen grid place-items-center px-4 py-12 bg-zinc-50" data-testid="invitation-accept-page">
      <div className="w-full max-w-md bg-white border border-zinc-200 rounded-2xl p-8 shadow-sm">
        <div className="w-12 h-12 rounded-xl bg-zinc-950 grid place-items-center mb-5">
          <Users size={20} className="text-white" />
        </div>
        {loading && (
          <div className="text-center" data-testid="invitation-loading">
            <Loader2 className="animate-spin mx-auto text-zinc-400" size={20} />
            <p className="text-sm text-zinc-500 mt-3">Loading invitation…</p>
          </div>
        )}
        {!loading && error && !accepted && (
          <div data-testid="invitation-error">
            <div className="flex items-center gap-2 text-rose-700 mb-2">
              <AlertCircle size={16} />
              <p className="font-semibold">Invitation unavailable</p>
            </div>
            <p className="text-sm text-zinc-600">{error}</p>
            <Link to="/dashboard" className="text-sm text-zinc-950 underline mt-4 inline-block">
              Go to dashboard
            </Link>
          </div>
        )}
        {!loading && invitation && !error && !accepted && (
          <>
            <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1">Workspace invitation</p>
            <h1 className="font-display text-2xl font-semibold text-zinc-950 tracking-tight">
              Join <span data-testid="invitation-workspace-name">{invitation.workspace_name}</span>
            </h1>
            <p className="text-sm text-zinc-600 mt-3">
              You were invited as <strong data-testid="invitation-role">{invitation.role}</strong>.
            </p>
            <div className="mt-4 px-3 py-2 rounded-lg bg-zinc-50 border border-zinc-200">
              <p className="text-xs text-zinc-500">Invitation address</p>
              <p className="text-sm text-zinc-900 font-medium" data-testid="invitation-email">{invitation.email}</p>
            </div>
            {isEmailMismatch && (
              <div className="mt-4 px-3 py-2 rounded-lg border border-amber-200 bg-amber-50 text-xs text-amber-800" data-testid="invitation-email-mismatch">
                You are signed in as <strong>{user.email}</strong>, but this invitation is for{" "}
                <strong>{invitation.email}</strong>. Sign out and sign in with the matching address.
              </div>
            )}
            <Button
              onClick={handleAccept}
              disabled={accepting || isEmailMismatch}
              data-testid="accept-invitation-button"
              className="w-full mt-6 h-11 bg-zinc-950 hover:bg-zinc-800 text-white rounded-lg disabled:opacity-50"
            >
              {accepting ? "Joining…" : "Accept invitation"}
            </Button>
          </>
        )}
        {accepted && (
          <div className="text-center" data-testid="invitation-success">
            <CheckCircle2 className="mx-auto text-emerald-500" size={28} />
            <p className="text-base font-semibold text-zinc-950 mt-3">Welcome aboard!</p>
            <p className="text-sm text-zinc-500 mt-1">Redirecting to the dashboard…</p>
          </div>
        )}
      </div>
    </div>
  );
}
